import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal, DestroyRef } from "@angular/core";
import { SelectOption, SelectOptionWithSelected } from "../../../shared/interfaces/select-option";
import { finalize, tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LoadingService } from "../../../shared/services/loading.service";
import { FileService } from "../../../shared/services/file.service";
import { MessageService } from "../../../shared/services/message.service";
import { NocsReport } from "../return-nocs-report/return-nocs-report.component";
import { FundLedgerReport } from "../models/fund-ledger-report";
import { FundLedgerReportResponse } from "../models/fund-ledger-report-response";
import { FundLedgerReportPost } from "../models/fund-ledger-report-post";

@Injectable({
    providedIn: 'root'
})
export class ScoReportingService {
    private api: string = 'banking/scoreporting/';
    #httpClient = inject(HttpClient);
    #loadingService = inject(LoadingService);
    #messageService = inject(MessageService);
    #fileService = inject(FileService);

    private destroyRef = inject(DestroyRef);

    private _scoReportingTransactionTypes = signal<SelectOption[]>([]);
    private _fundLedgerReport = signal<FundLedgerReport[] | null>(null);
    private _returnNocReport = signal<NocsReport[] | null>(null);
    private _totalTransactionRecords = signal<number>(0);

    readonly scoReportingTransactionTypes = this._scoReportingTransactionTypes.asReadonly();
    readonly fundLedgerReport = this._fundLedgerReport.asReadonly();
    readonly returnNocReport = this._returnNocReport.asReadonly();
    readonly totalTransactionRecords = this._totalTransactionRecords.asReadonly();

    readonly scoReportingTransactionTypesWithSelect = computed(() => {
        return this._scoReportingTransactionTypes().map((option) => {
            return {
                ...option,
                selected: false
            } as SelectOptionWithSelected;
        });
    });

    setScoReportingTransactionTypes(value: SelectOption[]): void {
        this._scoReportingTransactionTypes.set(value);
    }

    setTransactionTotalRecordSignal(value: number) {
        this._totalTransactionRecords.set(value);
    }

    setFundLedgerReport(value: FundLedgerReport[] | null): void {
        this._fundLedgerReport.set(value);
    }

    setReturnNocReport(value: NocsReport[] | null): void {
        this._returnNocReport.set(value);
    }

    getTransactionTypes() {
        return this.#httpClient.get<SelectOption[]>(`${this.api}transaction-types`).pipe(
            tap(response => {
                this._scoReportingTransactionTypes.set(response);
            })
        );
    }

    getFundLedgerReport(fundLedgerReportPost: FundLedgerReportPost) {
        this.#loadingService.showLoadingIndicator();
        return this.#httpClient.post<FundLedgerReportResponse>(`${this.api}fund-ledger-report`, fundLedgerReportPost).subscribe({
            next: (response) => {
                this._fundLedgerReport.set(response.items);
                this._totalTransactionRecords.set(response.totalRecords);
            },
            complete: () => {
                this.#loadingService.hideLoadingIndicator();
            }
        });
    }

    printFundLedgerReportExcel(startDate: Date, endDate: Date, agencyIds: number[], fundIds: number[], transactionTypeIds: number[], warrantNumber?: number, amount?: number) {
        const type = 'excel';
        this.#loadingService.showLoadingIndicator();
        return this.#fileService.downloadFile(`${this.api}print-fund-ledger-report-excel`, { startDate, endDate, agencyIds, fundIds, transactionTypeIds, warrantNumber, amount }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response) => {
                this.#messageService.showSnackbarMessage({ message: response.message, messageType: response.messageType });
            },
            complete: () => {
                this.#loadingService.hideLoadingIndicator();
            }
        });
    }

    getReturnNocReport(beginDate: Date, endDate: Date) {
        this.#loadingService.showLoadingIndicator();
        this.#httpClient.get<NocsReport[]>(`${this.api}return-nocs-report`, {
            params: {
                beginDate: beginDate.toISOString(),
                endDate: endDate.toISOString()
            }
        }).pipe(finalize(() => this.#loadingService.hideLoadingIndicator()), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response) => {
                this._returnNocReport.set(response);
            }
        })
    }
}